/* eslint-disable */
// useNotifications — ONE query, ONE array. The badge is its length.
//
// ⚠️ There is deliberately no second query for the count. The requirement is
// that the number on the bell and the list of cards can never disagree, and the
// only way to guarantee that is to derive both from the same array. A separate
// `count` query is exactly how a badge starts lying: two round trips, two
// moments in time, two answers.
//
// RLS already scopes notification_recipients to the caller, so this query does
// not filter by user_id and must not try to — the server owns that.
//
// The only writes the UI performs are read_at stamps. The RLS UPDATE policy plus
// the column grant restrict writes to seen_at / read_at / acknowledged_at /
// archived_at on the caller's own rows; the client does not enforce that and
// must not assume it can write anything else.

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { supabase } from '../supabase';
import { usePortalPermissions, canAccessPortal } from './usePortalPermissions';
import { isOrdersRow } from '../components/system/briefing/briefingModel';

export const NOTIFICATIONS_QUERY_KEY = ['system', 'notifications', 'unread'];
const SENDERS_QUERY_KEY_ROOT = ['system', 'notifications', 'senders'];

// expires_at is filtered CLIENT-side. Volume is a handful of rows per person,
// and a stale-by-seconds server predicate is not worth the PostgREST gymnastics.
function notExpired(row, nowMs) {
  const exp = row?.notifications?.expires_at;
  if (!exp) return true;
  const t = Date.parse(exp);
  return Number.isNaN(t) ? true : t > nowMs;
}

export function useNotifications() {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notification_recipients')
        .select(`
          id,
          notification_id,
          seen_at,
          read_at,
          created_at,
          notifications (
            id, type_key, severity, is_mandatory, title, body,
            entity_type, entity_id, payload, created_by,
            expires_at, created_at,
            notification_types ( key, label_en, label_he, portal )
          )
        `)
        .is('read_at', null)
        .is('archived_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    // AUD-05 P1: 30 s → 5 min. Freshness does not come from staleness: NotificationHost
    // (mounted in every phase via AppOverlays) runs useNotificationChannel, which refetches
    // on every broadcast, when it subscribes and on every return to a visible tab, and
    // every write path invalidates this key. The short staleTime only made the ~6 mounts
    // that come and go between Home and the portals re-read it.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  // The one array. Everything below is derived from it — never re-queried.
  // Orders alerts belong to the Morning Briefing and to people authorised for Orders only.
  // Fail-closed while permissions load: no Orders row is shown to anyone until the answer is in.
  const perms = usePortalPermissions();
  const ordersAllowed = !perms.isLoading && canAccessPortal(perms.data, 'orders');

  const rows = useMemo(() => {
    const now = Date.now();
    return (query.data || []).filter((r) => r.notifications && notExpired(r, now) && (ordersAllowed || !isOrdersRow(r)));
  }, [query.data, ordersAllowed]);

  // The Orders rows go to the briefing, NOT to a corner card, the top bar or the blocking modal.
  const ordersRows = useMemo(() => rows.filter(isOrdersRow), [rows]);

  // ⚠️ ONE TIER, ONE SURFACE (P2b §2). Each severity renders on exactly one
  // place — the same message appearing twice reads as a bug and trains people to
  // dismiss it twice. So the corner card is now `normal` ONLY:
  //   silent    -> badge only, no surface
  //   normal    -> the corner card
  //   important -> the top bar, and NO card
  //   critical  -> the blocking modal, and NO card and NO bar
  // All four still count on the badge, which is unchanged.
  const cards = useMemo(
    () => rows.filter((r) => r.notifications.severity === 'normal' && !isOrdersRow(r)),
    [rows],
  );

  const important = useMemo(
    () => rows.filter((r) => r.notifications.severity === 'important' && !isOrdersRow(r)),
    [rows],
  );
  const critical = useMemo(
    () => rows.filter((r) => r.notifications.severity === 'critical' && !isOrdersRow(r)),
    [rows],
  );

  const unreadCount = rows.length;

  // Sender display names. A SEPARATE small lookup, and deliberately not part of
  // the count: user_profiles' base table is own-row/admin only, so a cross-user
  // name has to come from the user_profiles_public view. If it fails the cards
  // still render — the footer just falls back.
  const senderIds = useMemo(() => {
    const ids = new Set();
    rows.forEach((r) => { if (r.notifications.created_by) ids.add(r.notifications.created_by); });
    return [...ids].sort();
  }, [rows]);

  const sendersQuery = useQuery({
    queryKey: [...SENDERS_QUERY_KEY_ROOT, senderIds.join(',')],
    enabled: senderIds.length > 0,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_profiles_public')
        .select('id, display_name, email')
        .in('id', senderIds);
      if (error) throw error;
      const map = {};
      (data || []).forEach((u) => {
        map[u.id] = u.display_name || (u.email ? u.email.split('@')[0] : null);
      });
      return map;
    },
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });

  const markRead = useMutation({
    mutationFn: async (recipientId) => {
      const { error } = await supabase
        .from('notification_recipients')
        .update({ read_at: new Date().toISOString() })
        .eq('id', recipientId)
        .is('read_at', null);
      if (error) throw error;
      return recipientId;
    },
    onError: (err) => Sentry.captureException(err, { tags: { source: 'useNotifications.markRead' } }),
    onSettled: invalidate,
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const ids = rows.filter((r) => !isOrdersRow(r)).map((r) => r.id);   // Orders rows are cleared in the briefing only
      if (!ids.length) return 0;
      const { error } = await supabase
        .from('notification_recipients')
        .update({ read_at: new Date().toISOString() })
        .in('id', ids);
      if (error) throw error;
      return ids.length;
    },
    onError: (err) => Sentry.captureException(err, { tags: { source: 'useNotifications.markAllRead' } }),
    onSettled: invalidate,
  });

  return {
    rows,
    ordersRows,
    ordersAllowed,
    cards,
    important,
    critical,
    unreadCount,
    senderNames: sendersQuery.data || {},
    isLoading: query.isLoading,
    isPending: query.isPending,
    isError: query.isError,
    markRead: (id) => markRead.mutate(id),
    markAllRead: () => markAllRead.mutate(),
    refetch: invalidate,
  };
}
